import { useDataDogRUM } from '@/composables/useDataDogRUM';
import { useAuthStore } from '@/stores/auth';
import type { RouteLocationNormalized } from 'vue-router';
import { createRouter, createWebHistory, NavigationFailureType } from 'vue-router';
import { useKnowledgeStore } from '../modules/knowledge/stores/knowledgeStore';
import NotFound from '../views/404NotFound.vue';
import AcsView from '../views/AcsView.vue';
import ForgotPassword from '../views/ForgotPassword.vue';
import Login from '../views/Login.vue';
import ResetPassword from '../views/ResetPassword.vue';

// Get the base URL from environment variables
const BASE_URL = import.meta.env.VITE_APP_BASE_URL || '/';

const { init, trackPageView, addAction, addError } = useDataDogRUM();

const router = createRouter({
    history: createWebHistory(BASE_URL),
    routes: [
        {
            path: '/login',
            name: 'login',
            component: Login,
            meta: {
                title: 'Login - OvationCXM',
            },
            // Redirect to home if already authenticated
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();

                // Make sure auth store is initialized
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                // Redirect to home if already authenticated
                if (authStore.isAuthenticated) {
                    console.debug('Already authenticated, redirecting to home');
                    next('/');
                } else {
                    // Allow access to login page
                    next();
                }
            },
        },
        {
          path: '/auth/kb',
          name: 'authorize-kb',
          component: () => import('../views/KnowledgeAuthorize.vue'),
          beforeEnter: async (to, from, next) => {
            console.log('Login authorize route detected, bypassing authentication check')
            next()
            return
          },
          meta: {
            title: 'Login Authorize - OvationCXM'
          }
        },
        {
            path: '/',
            component: () => import('../views/RootLayout.vue'),
            // Protect all routes under root
            beforeEnter: async (to, from, next) => {
                // Allow ACS route to bypass authentication (it will handle its own flow)
                if (to.path === '/auth/saml/acs' || to.name === 'auth/saml/acs') {
                    console.debug('ACS route detected, bypassing authentication check');
                    next();
                    return;
                }

                const authStore = useAuthStore();
                // Make sure auth store is initialized
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                if (authStore.isAuthenticated) {
                    next();
                } else {
                    console.debug('Not authenticated, redirecting to login');
                    // save current route for redirect after login do it in a store
                    // set this to the full url path including query params and hash
                    const url = new URL(window.location.href);
                    const redirectPath = url.pathname + url.search + url.hash;
                    
                    // Only save redirect path if it's not a public route (login, password reset, etc.)
                    // This prevents circular navigation failures
                    if (redirectPath !== '/login' && 
                        !redirectPath.startsWith('/login?') &&
                        redirectPath !== '/passwordreset' && 
                        !redirectPath.startsWith('/passwordreset?')) {
                        authStore.redirectAfterLogin = redirectPath;
                    }
                    next('/login');
                }
            },
            children: [
                {
                    path: '',
                    redirect: '/inbox/home',
                },
                {
                    path: 'inbox',
                    name: 'inbox',
                    component: () => import('../modules/inbox/views/InboxView.vue'),
                    meta: {
                        title: 'Inbox - OvationCXM',
                    },
                    beforeEnter: (to, from, next) => {
                        // Backward compat: redirect old ?view=home URLs to the dedicated /inbox/home route
                        if (to.query.view === 'home') {
                            next('/inbox/home');
                            return;
                        }
                        // Check if user is navigating directly to /inbox without query parameters
                        if (to.path === '/inbox' && Object.keys(to.query).length === 0) {
                            // Check if there's a saved last inbox URL
                            import('../stores/cases').then(({ useCasesStore }) => {
                                const casesStore = useCasesStore();
                                const lastUrl = casesStore.getAndClearLastInboxUrl();

                                if (lastUrl) {
                                    // Navigate to the saved URL
                                    next(lastUrl);
                                } else {
                                    // Default to inbox home
                                    next('/inbox/home');
                                }
                            });
                            return;
                        }
                        // Allow normal navigation for other cases
                        next();
                    },
                    children: [
                        {
                            path: 'home',
                            name: 'inbox-home',
                            component: () => import('../modules/inbox/components/views-panel/InboxHome.vue'),
                            meta: {
                                title: 'Home - Inbox - OvationCXM',
                            },
                        },
                        {
                            path: 'cases/:id',
                            name: 'inbox-case-detail',
                            component: () => import('../modules/inbox/views/InboxCase.vue'),
                        },
                        {
                            path: 'tasks/:id',
                            name: 'inbox-task-detail',
                            component: () => import('../modules/inbox/views/InboxTask.vue'),
                        },
                        {
                            path: 'task/:id',
                            name: 'inbox-task-detail',
                            component: () => import('../modules/inbox/views/InboxTask.vue'),
                        },
                    ],
                },
                {
                    path: 'journeys',
                    name: 'journeys',
                    component: () => import('../views/JourneysView.vue'),
                    meta: {
                        title: 'Journeys - OvationCXM',
                    },
                },
                {
                    path: 'customers',
                    name: 'customers',
                    component: () => import('../modules/customer/views/CustomersView.vue'),
                    meta: {
                        title: 'Customers - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'customers-list',
                            component: () => import('../modules/customer/views/CustomersListView.vue'),
                            meta: {
                                title: 'Customers - OvationCXM',
                            },
                        },
                        {
                            path: 'home',
                            name: 'customers-home',
                            component: () => import('../modules/customer/views/CustomersHome.vue'),
                            meta: {
                                title: 'Customers Overview - OvationCXM',
                            },
                        },
                        {
                            path: 'locations',
                            name: 'customers-locations',
                            component: () => import('../modules/customer/views/LocationsListView.vue'),
                            meta: {
                                title: 'Locations - OvationCXM',
                            },
                        },
                        {
                            path: 'contacts',
                            name: 'customers-contacts',
                            component: () => import('../modules/customer/views/ContactsListView.vue'),
                            meta: {
                                title: 'Contacts - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'customers/:id',
                    name: 'customer-detail',
                    component: () => import('../modules/customer/views/CustomerView.vue'),
                    props: true,
                    meta: {
                        title: 'Customer Detail - OvationCXM',
                    },
                },
                {
                    path: 'customers/locations/:id',
                    name: 'location-detail',
                    component: () => import('../modules/customer/views/LocationView.vue'),
                    props: true,
                    meta: {
                        title: 'Location Detail - OvationCXM',
                    },
                },
                {
                    path: 'customers/contacts/:id',
                    name: 'contact-detail',
                    component: () => import('../modules/customer/views/ContactView.vue'),
                    props: true,
                    meta: {
                        title: 'Contact Detail - OvationCXM',
                    },
                },
                {
                    path: 'knowledge',
                    name: 'knowledge',
                    component: () => import('../modules/knowledge/views/KnowledgeView.vue'),
                    meta: {
                        title: 'Knowledge - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'Knowledge',
                            component: () => import('../modules/knowledge/components/list-view/KnowledgeList.vue'),
                            meta: {
                                title: 'Knowledge - OvationCXM',
                            },
                        },
                        {
                            path: 'articles/:id',
                            name: 'knowledge-article',
                            component: () => import('../modules/knowledge/views/ArticleView.vue'),
                            props: true,
                            meta: {
                                title: 'Article',
                            },
                        },
                    ],
                },
                {
                    path: 'reports',
                    name: 'reports',
                    component: () => import('../modules/Analytics/Views/ReportsView.vue'),
                    meta: {
                        title: 'Reports - OvationCXM',
                    },
                },
                {
                    path: 'reports/:id',
                    name: 'report-detail',
                    component: () => import('../modules/Analytics/Views/ReportDetailsView.vue'),
                    props: true,
                    meta: {
                        title: 'Report Detail - OvationCXM',
                    },
                },
                {
                    path: 'reports/:id/standard',
                    name: 'standard-report-detail',
                    component: () => import('../modules/Analytics/Views/StandardReportDetailsView.vue'),
                    props: true,
                    meta: {
                        title: 'Standard Report - OvationCXM',
                    },
                },
                {
                    path: 'analytics',
                    name: 'analytics',
                    component: () => import('../modules/Analytics/Views/AnalyticsView.vue'),
                    meta: {
                        title: 'Analytics - OvationCXM',
                    },
                },
                {
                    path: 'aistudio',
                    name: 'aistudio',
                    component: () => import('../modules/AIStudio/Views/AIStudioView.vue'),
                    meta: {
                        title: 'AI Studio - OvationCXM',
                    },
                    children: [
                        {
                            path: '',
                            name: 'aistudio-default',
                            redirect: '/aistudio/home',
                        },
                        {
                            path: 'home',
                            name: 'aistudio-home',
                            component: () => import('../modules/AIStudio/components/AIStudioHome.vue'),
                            meta: {
                                title: 'Home - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'agents',
                            name: 'aistudio-agents',
                            component: () => import('../modules/AIStudio/components/agents/AIStudioAgentList.vue'),
                            meta: {
                                title: 'Agents - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'branding',
                            name: 'aistudio-branding',
                            component: () => import('../modules/AIStudio/components/branding/AIStudioBranding.vue'),
                            meta: {
                                title: 'Branding - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'tools',
                            name: 'aistudio-tools',
                            component: () => import('../modules/AIStudio/components/AIStudioToolsList.vue'),
                            meta: {
                                title: 'Tools - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'tools/:toolId',
                            name: 'aistudio-tool-details',
                            component: () => import('../modules/AIStudio/components/tools/AIStudioToolsEscalateDetails.vue'),
                            meta: {
                                title: 'Tool Details - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'models',
                            name: 'aistudio-models',
                            component: () => import('../modules/AIStudio/components/AIStudioModelsHome.vue'),
                            meta: {
                                title: 'Models - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'observability',
                            name: 'aistudio-observability',
                            component: () => import('../modules/AIStudio/components/AIStudioPerformance.vue'),
                            meta: {
                                title: 'Observability - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'observability/traces/:name',
                            name: 'aistudio-trace-list',
                            component: () => import('../modules/AIStudio/components/performance/AIStudioTraceList.vue'),
                            props: true,
                            meta: {
                                title: 'Trace List - Observability - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'observability/traces/:name/:id',
                            name: 'aistudio-trace-detail',
                            component: () => import('../modules/AIStudio/components/performance/AIStudioTraceDetail.vue'),
                            props: true,
                            meta: {
                                title: 'Trace Details - Observability - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'agents/:id',
                            name: 'aistudio-agent-config',
                            component: () => import('../modules/AIStudio/components/agents/AIAgentConfigRouter.vue'),
                            props: true,
                            meta: {
                                title: 'Agent Configuration - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'evaluations',
                            name: 'aistudio-evaluations',
                            component: () => import('../modules/AIStudio/components/evaluations/AIStudioEvaluations.vue'),
                            meta: {
                                title: 'Evaluations - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'evaluations/datasets/:datasetName',
                            name: 'aistudio-dataset-detail',
                            component: () => import('../modules/AIStudio/components/evaluations/AIStudioDatasetDetail.vue'),
                            props: true,
                            meta: {
                                title: 'Dataset Details - Evaluations - AI Studio - OvationCXM',
                            },
                        },
                        {
                            path: 'evaluations/datasets/:datasetName/experiments/:experimentId',
                            name: 'aistudio-experiment-detail',
                            component: () => import('../modules/AIStudio/components/evaluations/AIStudioExperimentDetail.vue'),
                            props: true,
                            meta: {
                                title: 'Experiment Results - Evaluations - AI Studio - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'settings',
                    name: 'settings',
                    redirect: '/settings/home',
                    meta: {
                        title: 'Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields/tags/:tagType/:id',
                    name: 'settings-tag-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Tag Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields/tags/:tagType',
                    name: 'settings-tags-list',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Tags - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields/:subsection/:id',
                    name: 'settings-field-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Field Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/fields',
                    name: 'settings-fields',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    meta: {
                        title: 'Fields - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/templates/:type/:id',
                    name: 'settings-template-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Template Details - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/layouts/cards/:id',
                    name: 'settings-card-detail',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: 'Card Detail - Settings - OvationCXM',
                    },
                },
                {
                    path: 'settings/:section/:id?',
                    name: 'settings-section',
                    component: () => import('../modules/settings/views/SettingsView.vue'),
                    props: true,
                    meta: {
                        title: (route: RouteLocationNormalized) => {
                            const section = route.params.section;
                            const id = route.params.id;

                            if (section === 'layouts' && id) {
                                return 'Layout Detail - OvationCXM';
                            }

                            if (section) {
                                // Format section for display (capitalize and replace dashes with spaces)
                                const formattedSection = section
                                    .toString()
                                    .split('-')
                                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                                return `${formattedSection} Settings - OvationCXM`;
                            }
                            return 'Settings - OvationCXM';
                        },
                    },
                    children: [
                        {
                            path: 'submissions',
                            name: 'settings-form-submissions',
                            component: () => import('../modules/settings/components/forms/SettingsFormsSubmissionsList.vue'),
                            props: true,
                            meta: {
                                title: 'Form Submissions - OvationCXM',
                            },
                        },
                        {
                            path: 'submissions/:submissionId',
                            name: 'settings-form-submission-view',
                            component: () => import('../modules/settings/components/forms/SettingsFormsSubmissionView.vue'),
                            props: true,
                            meta: {
                                title: 'Form Submission View - OvationCXM',
                            },
                        },
                        {
                            path: 'webhooks/:webhookId',
                            name: 'settings-apis-webhook-details',
                            component: () => import('../modules/settings/views/SettingsView.vue'),
                            props: true,
                            meta: {
                                title: 'Webhook Details - API Settings - OvationCXM',
                            },
                        },
                        {
                            path: 'saml/:samlId',
                            name: 'settings-security-saml-details',
                            component: () => import('../modules/settings/views/SettingsView.vue'),
                            props: true,
                            meta: {
                                title: 'SAML Integration Details - Security Settings - OvationCXM',
                            },
                        },
                    ],
                },
                {
                    path: 'profile',
                    name: 'profile',
                    component: () => import('../views/UserProfile.vue'),
                    meta: {
                        title: 'User Profile - OvationCXM',
                    },
                },
            ],
        },
        {
            path: '/auth/saml/acs',
            name: 'auth/saml/acs',
            component: AcsView,
            meta: {
                title: 'ACS Login - OvationCXM',
            },
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: NotFound,
            meta: {
                title: '404 - Page Not Found',
            },
        },
        {
            path: '/passwordreset',
            name: 'passwordreset',
            component: ForgotPassword,
            meta: {
                title: 'Forgot Password - OvationCXM',
            },
            // Redirect to home if already authenticated
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();

                // Make sure auth store is initialized
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                // Redirect to home if already authenticated
                if (authStore.isAuthenticated) {
                    console.debug('Already authenticated, redirecting to login');
                    next('/login');
                } else {
                    // Allow access to login page
                    next();
                }
            },
        },
        {
            path: '/passwordchange/:token/:email',
            name: 'passwordchange',
            component: ResetPassword,
            meta: {
                title: 'Change Password - OvationCXM',
            },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();

                // Wait for the auth store to initialize if needed
                if (!authStore.isInitialized) {
                    await authStore.initialize();
                }

                // Redirect to login if already authenticated
                if (authStore.isAuthenticated) {
                    console.debug('Already authenticated, redirecting to login');
                    next('/login');
                } else {
                    next();
                }
            },
        },
    ],
});

router.beforeEach((to, from, next) => {
    // If we're navigating away from the knowledge base section
    if (from.path.startsWith('/knowledge') && !to.path.startsWith('/knowledge')) {
        const knowledgeStore = useKnowledgeStore();
        knowledgeStore.clearCurrentList();
        knowledgeStore.clearCurrentArticle();
    }
    // If we're navigating away from a specific article
    else if (from.name === 'knowledge-article' && to.name !== 'knowledge-article') {
        const knowledgeStore = useKnowledgeStore();
        knowledgeStore.clearCurrentArticle();
    }

    // Update document title based on route meta
    if (to.meta.title) {
        // Handle function or string title
        const title = typeof to.meta.title === 'function' ? to.meta.title(to) : to.meta.title;
        document.title = title as string;
    } else {
        // Default title
        document.title = 'OvationCXM';
    }

    next();
});

// Global router error handler
router.onError((error, to, from) => {
    console.error('🚨 Router navigation error:', error);
    
    // Check if this is a dynamic import failure
    const isDynamicImportError = 
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk');
    
    if (isDynamicImportError) {
        console.warn('🔄 Dynamic import failed, this should be handled by useRouterNavigation composable');
    }
    
    // Log the error with comprehensive metadata for debugging
    addError(error, {
        // Navigation context
        from: from?.fullPath || 'unknown',
        from_name: from?.name?.toString() || 'unknown',
        to: to?.fullPath || 'unknown',
        to_name: to?.name?.toString() || 'unknown',
        
        // Error details
        error_message: error?.message || 'Unknown error',
        error_name: error?.name || 'Error',
        error_stack: error?.stack || 'No stack trace available',
        error_cause: error?.cause ? String(error.cause) : undefined,
        
        // Error classification
        isDynamicImportError,
        error_source: 'router',
        
        // Timestamp
        timestamp: new Date().toISOString(),
    });
});

router.afterEach((to, from, failure) => {
    if (!failure) {
        // Track page view with route name and path
        trackPageView(to.path, to.name?.toString());

        // Log navigation action
        addAction('navigation', {
            from: from.fullPath,
            to: to.fullPath,
            routeName: to.name?.toString(),
        });
    }
    // Note: Navigation failures (duplicated, cancelled, aborted) are not logged to Datadog
    // as they are expected behaviors, not actual errors. Real errors like missing components
    // or dynamic import failures are handled by router.onError() above.
});

export default router;
